/**
 * useForensicAccess — Forensic investigation mode for authorized roles.
 *
 * When active, bypasses all data masking to reveal raw CCTV timestamps,
 * transaction IDs, and employee names. Only available to super_admin,
 * tenant_admin, and regional_manager.
 *
 * All forensic access is logged for audit compliance.
 *
 * @module hooks/useForensicAccess
 */

import { useState, useCallback, useMemo, createContext, useContext } from 'react';
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';

// ── Types ──────────────────────────────────────────────────────

export interface ForensicAccessLog {
  /** When forensic mode was activated */
  activatedAt: string;
  /** Who activated it */
  userId: string;
  /** Role of the user */
  role: UserRole;
  /** Module context (e.g., 'forensics', 'alerts', 'pos') */
  context: string;
  /** What records were accessed (tracked as IDs) */
  accessedRecordIds: string[];
}

export interface ForensicAccessState {
  /** Whether forensic mode is currently active */
  isForensicMode: boolean;
  /** Whether the current user can activate forensic mode */
  canActivateForensicMode: boolean;
  /** Toggle forensic mode on/off */
  toggleForensicMode: () => void;
  /** Activate forensic mode for a specific context */
  activateForensicMode: (context: string) => void;
  /** Deactivate forensic mode */
  deactivateForensicMode: () => void;
  /** Log that a specific record was viewed in forensic mode */
  logRecordAccess: (recordId: string) => void;
  /** Get the current session's access log */
  accessLog: ForensicAccessLog | null;
  /** Current user's role */
  userRole: UserRole;
}

// ── Authorized Roles ─────────────────────────────────────────

const FORENSIC_AUTHORIZED_ROLES: Set<UserRole> = new Set([
  'super_admin',
  'tenant_admin',
]);

// ── Context ──────────────────────────────────────────────────

const ForensicAccessContext = createContext<ForensicAccessState | null>(null);

interface ForensicAccessProviderProps {
  children: React.ReactNode;
}

export const ForensicAccessProvider: React.FC<ForensicAccessProviderProps> = ({ children }) => {
  const { tenant, user } = useAuth();
  const role: UserRole = tenant?.role ?? 'store_manager';
  const canActivate = FORENSIC_AUTHORIZED_ROLES.has(role);

  const [isActive, setIsActive] = useState(false);
  const [accessLog, setAccessLog] = useState<ForensicAccessLog | null>(null);

  const activateForensicMode = useCallback((context: string) => {
    if (!canActivate) return;
    setIsActive(true);
    setAccessLog({
      activatedAt: new Date().toISOString(),
      userId: user?.id ?? 'unknown',
      role,
      context,
      accessedRecordIds: [],
    });
  }, [canActivate, user?.id, role]);

  const deactivateForensicMode = useCallback(() => {
    setIsActive(false);
    // Keep the log for the session (could be persisted to DB for audit)
  }, []);

  const toggleForensicMode = useCallback(() => {
    if (isActive) {
      deactivateForensicMode();
    } else {
      activateForensicMode('general');
    }
  }, [isActive, activateForensicMode, deactivateForensicMode]);

  const logRecordAccess = useCallback((recordId: string) => {
    if (!isActive || !accessLog) return;
    setAccessLog(prev => {
      if (!prev) return prev;
      if (prev.accessedRecordIds.includes(recordId)) return prev;
      return {
        ...prev,
        accessedRecordIds: [...prev.accessedRecordIds, recordId],
      };
    });
  }, [isActive, accessLog]);

  const value: ForensicAccessState = useMemo(() => ({
    isForensicMode: isActive && canActivate,
    canActivateForensicMode: canActivate,
    toggleForensicMode,
    activateForensicMode,
    deactivateForensicMode,
    logRecordAccess,
    accessLog,
    userRole: role,
  }), [isActive, canActivate, toggleForensicMode, activateForensicMode, deactivateForensicMode, logRecordAccess, accessLog, role]);

  return (
    <ForensicAccessContext.Provider value={value}>
      {children}
    </ForensicAccessContext.Provider>
  );
};

export function useForensicAccess(): ForensicAccessState {
  const ctx = useContext(ForensicAccessContext);
  if (!ctx) {
    throw new Error('useForensicAccess must be used within <ForensicAccessProvider>');
  }
  return ctx;
}

export default ForensicAccessContext;
